// @ts-strict-ignore
import {AttrsSchema, Component} from 'panel';

import {defineAndMakeJsxFactory} from 'common/util/jsx';
import {apiClient} from 'common/util/api-client';
import {initMixpanel} from 'common/report/tracking-setup';
import {reporter} from 'common/report/error-reporting';
import {promisifyModal} from 'common/components/modal/utils';
import {getLocation} from 'common/util/url';
import {getProjectId} from 'common/report/util';
import {addToast} from 'common/components/toast-area/toast-manager';
import {ToastType} from 'common/components/toast/types';
import {Section, SectionItem} from 'common/components/items-menu/types';
import {IronContextRegistry} from 'common/report/context-registry';
import {Translatable} from 'common/i18n';
import {InputInputEvent} from 'common/components/input/types';

import {FeedbackForFeature, Sentiment} from './types';
import {FeedbackType} from './types';
import {Template as template} from './template';
import css from './index.inline.styl';

type FeedbackModalAttrs = {
  feature: FeedbackForFeature;
  'negative-label'?: string;
  'positive-label'?: string;
  'sentiment-heading'?: string;
  'submit-button-text'?: string;
  'talk-to-pm-override'?: string;
  title?: string;
  placeholder?: string;
};

type FeedbackModalParams = {
  extraFeedbackEventName?: string;
  extraFeedbackEventProperties?: Record<string, any>;
  feedbackText?: Translatable;
  initialSentiment?: Sentiment;
  langfuseTraceUrl?: string;
};

type FeedbackModalState = {
  feedback: string;
  customerEmail?: string;
  sfdcOpportunityId?: string;
  feedbackType?: FeedbackType;
  sentiment?: Sentiment;
  saving: boolean;
  submitErrorMsg: string;
};

type FeedbackModalJsxProps = {
  attrs: FeedbackModalAttrs;
  on: {
    close: (ev: CustomEvent) => void;
  };
  params: FeedbackModalParams;
};

export class FeedbackModal extends Component<
  FeedbackModalState,
  FeedbackModalAttrs,
  unknown,
  unknown,
  IronContextRegistry,
  FeedbackModalParams
> {
  mixpanel = null;

  static get attrsSchema(): AttrsSchema<FeedbackModalAttrs> {
    return {
      feature: {
        type: `string`,
        description: `An identifier for the feature the user is providing feedback on. E.g. "live view 2021"`,
      },
      'negative-label': {type: `string`},
      placeholder: {
        type: `string`,
        description: `The placeholder text shown in the textarea`,
      },
      'positive-label': {type: `string`},
      'sentiment-heading': {type: `string`},
      'submit-button-text': {type: `string`},
      'talk-to-pm-override': {type: `string`},
      title: {type: `string`},
    };
  }

  constructor() {
    super();
    this.mixpanel = initMixpanel(PROJECT_3_MIXPANEL_TOKEN);
  }

  get config() {
    return {
      css,
      template,
      useShadowDom: true,
      contexts: [`currentUser` as const, `i18n` as const],
      defaultState: {
        feedback: ``,
        customerEmail: null,
        feedbackType: null,
        sfdcOpportunityId: null,
        saving: false,
        submitErrorMsg: null,
      },
      params: {
        extraFeedbackEventName: String,
        extraFeedbackEventProperties: Object,
        feedbackText: Function,
        initialSentiment: String,
        langfuseTraceUrl: String,
      },
    };
  }

  get t() {
    return this.getContext(`i18n`).t;
  }

  get title() {
    return this.attr(`title`) || this.t(`help.submit-feedback`);
  }

  get placeholder() {
    return this.attr(`placeholder`) || this.t(`help.feedback.placeholder`);
  }

  get sentimentHeading() {
    return this.attr(`sentiment-heading`) || this.t(`help.feedback.sentiment-prompt`);
  }

  get positiveLabel() {
    return this.attr(`positive-label`) || this.t(`help.feedback.positive`);
  }

  get negativeLabel() {
    return this.attr(`negative-label`) || this.t(`help.feedback.negative`);
  }

  get submitButtonText() {
    return this.attr(`submit-button-text`) || this.t(`help.feedback.submit`);
  }

  handleFeedbackInput = (value: string, ev: InputInputEvent) => {
    ev.stopPropagation();
    this.update({feedback: value});
  };

  handleCustomerEmailInput = (value: string, ev: InputInputEvent) => {
    ev.stopPropagation();
    this.update({customerEmail: value});
  };

  handleSfdcOpportunityId = (value: string, ev: InputInputEvent) => {
    ev.stopPropagation();
    this.update({sfdcOpportunityId: value});
  };

  getFeedbackTypeSections(): Section[] {
    return [
      {
        items: [
          {value: null, label: this.t(`help.feedback.type-unspecified`)},
          {value: FeedbackType.Dealbreaker, label: this.t(`help.feedback.type-dealbreaker`)},
          {value: FeedbackType.PainPoint, label: this.t(`help.feedback.type-pain-point`)},
          {value: FeedbackType.Beta, label: this.t(`help.feedback.type-alpha-beta`)},
          // Removed NotWorking option at request of product but leaving commented out
          // since the db still has this value for historical purposes and I want to make it clear it's intentional
          // {value: FeedbackType.NotWorking, label: `Not working as expected (e.g. bugs)`},
        ].map((option) => {
          return {...option, isSelected: option.value === this.state.feedbackType};
        }),
      },
    ];
  }

  handleSelectFeedbackType = (item: SectionItem) => {
    this.update({feedbackType: item.value});
  };

  handleModalChange = (ev: CustomEvent) => {
    ev.stopPropagation();
    if (ev.detail && ev.detail.state === `closed`) {
      this.closeModal();
    }
  };

  closeModal = (submitted = false) => {
    this.dispatchEvent(new CustomEvent(`close`, {detail: {submitted, sentiment: this.state.sentiment}}));
  };

  isStaffUser = () => {
    return this.getContext(`currentUser`).get().value?.isStaff;
  };

  /**
   * Determines if the feedback can be submitted on behalf of another user.
   */
  canDelegateFeedback = () => {
    // Partners are allowed to submit feedback on behalf of others.
    const PARTNER_DOMAINS = [
      `mfitlab.com`,
      `human37.com`,
      `mammothgrowth.com`,
      `brooklyndata.co`,
      `bildungdata.com`,
      `hemodata.me`,
      `omtera.com`,
      `nttcomms.com`,
      `growthanalyticsmarketing.com`,
    ];

    const currentEmail = this.getContext(`currentUser`).get().value?.email || ``;
    const emailDomain = currentEmail?.split(`@`)[1];

    return this.isStaffUser() || PARTNER_DOMAINS.includes(emailDomain?.toLowerCase());
  };

  submitFeedback = async () => {
    try {
      this.update({saving: true, submitErrorMsg: null});

      await apiClient.post(`/api/app/projects/${getProjectId()}/product-feedback`, {
        feature: this.attr(`feature`),
        feedback: this.state.feedback,
        sentiment: this.state.sentiment,
        feedback_type: this.state.feedbackType,
        customer_email: this.state.customerEmail,
        sfdc_opportunity_id: this.state.sfdcOpportunityId,
        url: getLocation().href,
        session_replay_url: this.mixpanel.get_session_replay_url(),
        langfuse_trace_url: this.params.langfuseTraceUrl,
      });
      const feedbackProps = {
        customer_email: this.state.customerEmail,
        sfdc_opportunity_id: this.state.sfdcOpportunityId,
        feature: this.attr(`feature`),
        feedback: this.state.feedback,
        feedback_type: this.state.feedbackType,
        is_staff_user: this.isStaffUser(),
        sentiment: this.state.sentiment,
      };
      this.track(`Product Feedback Submitted`, feedbackProps);

      // Submit an extra event if one was passed.
      if (this.params.extraFeedbackEventName) {
        this.track(this.params.extraFeedbackEventName, {
          ...feedbackProps,
          ...this.params.extraFeedbackEventProperties,
        });
      }

      this.closeModal(true);
      // this brings up the success toast
      addToast({
        message: this.t(`help.feedback.thank-you`),
        hide: false,
        type: ToastType.SUCCESS,
      });
    } catch (err) {
      this.update({submitErrorMsg: (err as Error).message});
    } finally {
      this.update({saving: false});
    }
  };

  track(eventName: string, properties = {}): void {
    try {
      this.mixpanel.track(eventName, properties);
    } catch (err) {
      reporter.error(`tracking error`, err);
    }
  }

  connectedCallback() {
    super.connectedCallback();
    const print = this.getContext(`i18n`).print;
    this.track(`Product Feedback Modal Opened`, {
      feature: this.attr(`feature`),
    });
    if (this.params.initialSentiment) {
      this.update({sentiment: this.params.initialSentiment});
    }
    if (this.params.feedbackText) {
      this.update({feedback: print(this.params.feedbackText)});
    }
  }
}

const FeedbackModalEl = defineAndMakeJsxFactory<FeedbackModalJsxProps>(`mp-feedback-modal`, FeedbackModal);
export async function promptFeedbackModal(
  attrs: FeedbackModalAttrs,
  params: FeedbackModalParams = {},
): Promise<[boolean, Sentiment]> {
  const closeEv = await promisifyModal({
    panelElement: FeedbackModalEl,
    attrs,
    params,
    closeEvents: [`close`],
  });

  return [closeEv.detail.submitted, closeEv.detail.sentiment];
}
